import { useApolloClient } from '@apollo/client';
import {
  type VendorCatalogBrowseResult,
  type VendorCatalogProduct,
} from '@choco-admin/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  SuppliersPageVendorItemUnitsForVendorSearchDocument,
  SuppliersPageVendorItemsForVendorDocument,
  useSuppliersPageVendorItemUnitsForVendorSearchQuery,
  useSuppliersPageVendorItemsForVendorQuery,
  type SuppliersPageVendorItemUnitsForVendorSearchQuery,
  type SuppliersPageVendorItemUnitsForVendorSearchQueryVariables,
  type SuppliersPageVendorItemsForVendorQuery,
  type SuppliersPageVendorItemsForVendorQueryVariables,
  type VendorItemUnitsForVendorSearchInput,
  type VendorItemsForVendorInput,
} from '../../../../../../../generated';
import {
  isLoadMoreResponseFresh,
  shouldSeedPageOne,
} from '../utils/vendorCatalogBrowseState';
import {
  isCatalogSearchMode,
  mapVendorCatalogResponse,
} from '../utils/vendorCatalogMapper';

const PAGE_SIZE = 25;

interface UseVendorCatalogBrowseParams {
  vendorId?: string;
  /** Already-debounced search term. */
  searchTerm: string;
  /** Selected custom (sub-)catalog id, or `undefined` for "all". */
  subCatalogId?: string;
}

interface UseVendorCatalogBrowseResult {
  products: VendorCatalogProduct[];
  totalCount: number;
  /** First page is loading (nothing to show yet). */
  isLoading: boolean;
  /** A "Load more" page is loading. */
  isLoadingMore: boolean;
  errorMessage: string | null;
  hasNextPage: boolean;
  loadMore: () => void;
}

/**
 * Read-only browse of a distributor's LOX catalog products. Uses
 * `vendorItemsForVendor` for the default browse and
 * `vendorItemUnitsForVendorSearch` once a search term is typed — the
 * replacement for the deprecated `browseCatalog` query. Accumulates pages
 * locally for a cursor-based "Load more".
 */
export const useVendorCatalogBrowse = ({
  vendorId,
  searchTerm,
  subCatalogId,
}: UseVendorCatalogBrowseParams): UseVendorCatalogBrowseResult => {
  const client = useApolloClient();
  const searchMode = isCatalogSearchMode(searchTerm);
  const enabled = Boolean(vendorId);
  const trimmedSearch = searchTerm.trim();
  const customCatalogId = subCatalogId || undefined;

  const buildBrowseInput = useCallback(
    (cursor?: string): VendorItemsForVendorInput => ({
      vendorId: vendorId ?? '',
      filter: customCatalogId ? { customCatalogId } : undefined,
      sort: { field: 'name', order: 'asc' },
      pagination: { limit: PAGE_SIZE, direction: 'forward', cursor },
    }),
    [vendorId, customCatalogId]
  );

  const buildSearchInput = useCallback(
    (cursor?: string): VendorItemUnitsForVendorSearchInput => ({
      vendorId: vendorId ?? '',
      searchQuery: trimmedSearch,
      filter: customCatalogId ? { customCatalogId } : undefined,
      pagination: { limit: PAGE_SIZE, direction: 'forward', cursor },
    }),
    [vendorId, trimmedSearch, customCatalogId]
  );

  const browse = useSuppliersPageVendorItemsForVendorQuery({
    variables: { input: buildBrowseInput() },
    skip: !enabled || searchMode,
    // no-cache: the VendorItemsForVendorResponse union members carry no `id`,
    // so InMemoryCache can't normalise them and a cache read drops the
    // inline-fragment `edges`. Reading straight from the network keeps the
    // response intact (load-more below already uses no-cache for the same
    // reason).
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const search = useSuppliersPageVendorItemUnitsForVendorSearchQuery({
    variables: { input: buildSearchInput() },
    skip: !enabled || !searchMode,
    // no-cache — same union-normalisation reason as the browse query above.
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const mapped = useMemo(
    () =>
      mapVendorCatalogResponse(
        searchMode
          ? search.data?.vendorItemUnitsForVendorSearch
          : browse.data?.vendorItemsForVendor
      ),
    [searchMode, search.data, browse.data]
  );

  const signature = `${searchMode ? 'search' : 'browse'}::${vendorId ?? ''}::${
    customCatalogId ?? ''
  }::${searchMode ? trimmedSearch : ''}`;

  const activeLoading = searchMode ? search.loading : browse.loading;

  const [products, setProducts] = useState<VendorCatalogProduct[]>([]);
  const [pageInfo, setPageInfo] = useState<{
    hasNextPage: boolean;
    endCursor: string;
  } | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const seededSignature = useRef<string | null>(null);
  const seededResult = useRef<VendorCatalogBrowseResult | null>(null);
  // Live signature, read by in-flight "Load more" responses to detect that the
  // query identity changed while the request was in flight.
  const liveSignature = useRef(signature);

  useEffect(() => {
    liveSignature.current = signature;
  }, [signature]);

  // Reset the accumulated list whenever the query identity changes. Clearing
  // `isLoadingMore` here means a filter change mid-load-more doesn't leave the
  // button spinning; the stale response is dropped by the signature check.
  useEffect(() => {
    seededSignature.current = null;
    seededResult.current = null;
    setProducts([]);
    setPageInfo(null);
    setTotalCount(0);
    setIsLoadingMore(false);
  }, [signature]);

  // (Re)seed page one whenever a fresh network completion lands for the current
  // query. Gating on `!activeLoading` ignores Apollo's synchronous same-variable
  // replay (which arrives with `loading: true`) so the fresh `no-cache` response
  // always wins and the list never latches on a stale page 1.
  useEffect(() => {
    if (
      !shouldSeedPageOne({
        loading: activeLoading,
        result: mapped,
        signature,
        seededSignature: seededSignature.current,
        seededResult: seededResult.current,
      })
    ) {
      return;
    }
    // `shouldSeedPageOne` guarantees a success result here.
    if (!mapped || mapped.kind !== 'success') {
      return;
    }
    seededSignature.current = signature;
    seededResult.current = mapped;
    // Replace page 1; any accumulated "Load more" pages were relative to the
    // previous page 1 and are intentionally discarded.
    setProducts(mapped.page.products);
    setPageInfo(mapped.page.pageInfo);
    setTotalCount(mapped.page.totalCount);
  }, [mapped, signature, activeLoading]);

  const loadMore = useCallback(() => {
    if (
      !enabled ||
      !pageInfo?.hasNextPage ||
      !pageInfo.endCursor ||
      isLoadingMore
    ) {
      return;
    }
    const cursor = pageInfo.endCursor;
    // Capture the query identity at request start; the response is dropped if
    // the operator changes filter / search / mode before it resolves.
    const requestSignature = liveSignature.current;
    setIsLoadingMore(true);

    const request = searchMode
      ? client.query<
          SuppliersPageVendorItemUnitsForVendorSearchQuery,
          SuppliersPageVendorItemUnitsForVendorSearchQueryVariables
        >({
          query: SuppliersPageVendorItemUnitsForVendorSearchDocument,
          variables: { input: buildSearchInput(cursor) },
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        })
      : client.query<
          SuppliersPageVendorItemsForVendorQuery,
          SuppliersPageVendorItemsForVendorQueryVariables
        >({
          query: SuppliersPageVendorItemsForVendorDocument,
          variables: { input: buildBrowseInput(cursor) },
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        });

    request
      .then((response) => {
        // Drop a stale response: the filter / search / mode changed while this
        // page was in flight, so appending it would poison the list and the
        // cursor. The reset effect already cleared `isLoadingMore`.
        if (!isLoadMoreResponseFresh(requestSignature, liveSignature.current)) {
          return response;
        }
        const nextPage = mapVendorCatalogResponse(
          'vendorItemUnitsForVendorSearch' in response.data
            ? response.data.vendorItemUnitsForVendorSearch
            : response.data.vendorItemsForVendor
        );
        if (nextPage?.kind === 'success') {
          setProducts((prev) => [...prev, ...nextPage.page.products]);
          setPageInfo(nextPage.page.pageInfo);
        }
        return response;
      })
      .catch(() => {
        // A failed "Load more" leaves the already-loaded pages intact; the
        // operator can retry. First-page errors are surfaced via errorMessage.
      })
      .finally(() => {
        // Only clear the spinner for the live query — a stale response must not
        // stop the spinner of a "Load more" that was started after the reset.
        if (isLoadMoreResponseFresh(requestSignature, liveSignature.current)) {
          setIsLoadingMore(false);
        }
      });
  }, [
    enabled,
    pageInfo,
    isLoadingMore,
    searchMode,
    client,
    buildSearchInput,
    buildBrowseInput,
  ]);

  const apolloError = searchMode ? search.error : browse.error;

  const errorMessage =
    mapped?.kind === 'error' ? mapped.message : (apolloError?.message ?? null);

  const isLoading =
    enabled && activeLoading && products.length === 0 && !errorMessage;

  return {
    products,
    totalCount,
    isLoading,
    isLoadingMore,
    errorMessage,
    hasNextPage: Boolean(pageInfo?.hasNextPage),
    loadMore,
  };
};
